import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Snackbar,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { SidebarLayout } from '@/components/Layout';
import { AuthGuard } from '@/components/common/AuthGuard';
import { useInquiryNotifications } from '@/hooks/useInquiryNotifications';
import { useHostInquiries, useUpdateInquiryStatus, BookingInquiry } from '@/hooks/api/useBookingInquiries';
import { useCreateBooking } from '@/hooks/api/useBookings';
import { useListings } from '@/hooks/api/useListings';
import { BookingForm } from '@/components/Bookings/BookingForm';
import { BookingForm as BookingFormData } from '@/utils/bookings-service';

const InquiriesPage: React.FC = () => {
  const { markAsSeen, refreshNotifications } = useInquiryNotifications();

  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInquiry, setSelectedInquiry] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [bookingFormOpen, setBookingFormOpen] = useState(false);
  const [inquiryToConfirm, setInquiryToConfirm] = useState<BookingInquiry | null>(null);

  // Use the proper hooks for data fetching and mutations
  const { data: inquiries = [], isLoading, error: queryError, refetch: refetchInquiries } = useHostInquiries();
  const { data: listings = [] } = useListings({ status: 'published' });
  const updateInquiryStatusMutation = useUpdateInquiryStatus();
  const createBookingMutation = useCreateBooking();

  const error = queryError ? 'Failed to load inquiries. Please try again.' : null;

  // Update inquiry status using the proper hook
  const updateInquiryStatus = useCallback(async (inquiryId: string, status: 'responded' | 'declined') => {
    try {
      await updateInquiryStatusMutation.mutateAsync({ inquiryId, status });

      // Refresh notifications to update badge counts
      refreshNotifications();

      // Close menu
      setAnchorEl(null);
      setSelectedInquiry(null);
    } catch (error) {
      console.error('Error updating inquiry status:', error);
      // The error will be handled by the mutation hook
    }
  }, [updateInquiryStatusMutation, refreshNotifications]);

  // Open booking confirmation modal
  const openBookingConfirmation = useCallback((inquiry: BookingInquiry) => {
    setInquiryToConfirm(inquiry);
    setBookingFormOpen(true);
    
    // Close menu
    setAnchorEl(null);
    setSelectedInquiry(null);
  }, []);

  // Handle booking form submission
  const handleBookingSubmit = useCallback(async (bookingData: BookingFormData) => {
    if (!inquiryToConfirm) return;

    try {
      // Convert BookingFormData to the format expected by useCreateBooking
      const createBookingData = {
        listingId: bookingData.listingId,
        startDate: bookingData.startDate,
        endDate: bookingData.endDate,
        guests: bookingData.guests,
        guestName: bookingData.guestName,
        guestEmail: bookingData.guestEmail,
        totalPrice: bookingData.totalPrice,
        primaryGuestContact: {
          name: bookingData.guestName,
          email: bookingData.guestEmail,
          whatsappNumber: bookingData.primaryGuestContact.whatsappNumber,
          preferredMethod: bookingData.primaryGuestContact.preferredMethod,
        },
        expectedGuests: bookingData.expectedGuests,
      };

      console.log('Creating booking for inquiry:', inquiryToConfirm.inquiryId);
      
      // Step 1: Create the booking with converted data
      const newBooking = await createBookingMutation.mutateAsync(createBookingData);
      console.log('Booking created successfully:', newBooking.bookingId);

      // Step 2: Update inquiry status to confirmed
      console.log('Updating inquiry status to confirmed...');
      await updateInquiryStatusMutation.mutateAsync({
        inquiryId: inquiryToConfirm.inquiryId,
        status: 'confirmed'
      });
      console.log('Inquiry status updated successfully');

      // Refresh notifications to update badge counts
      refreshNotifications();
      
      // Manually refresh inquiries list to ensure UI updates
      refetchInquiries();

      // Close modal and reset state
      setBookingFormOpen(false);
      setInquiryToConfirm(null);

      // Show success message
      setSuccessMessage(`Booking confirmed for ${inquiryToConfirm.guestInfo.name}! Booking ID: ${newBooking.bookingId}`);
      setShowSuccess(true);

    } catch (error) {
      console.error('Error in booking confirmation process:', error);
      
      // More specific error handling
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      
      // Show error message to user
      setSuccessMessage(`Failed to confirm booking: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setShowSuccess(true);
    }
  }, [inquiryToConfirm, createBookingMutation, updateInquiryStatusMutation, refreshNotifications, refetchInquiries]);

  // Handle booking form close
  const handleBookingFormClose = useCallback(() => {
    setBookingFormOpen(false);
    setInquiryToConfirm(null);
  }, []);

  // Handle menu actions
  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>, inquiryId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedInquiry(inquiryId);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedInquiry(null);
  }, []);

  // Filter inquiries by status
  const filteredInquiries = selectedStatus === 'all'
    ? inquiries
    : inquiries.filter(inquiry => inquiry.status === selectedStatus);

  // Mark notifications as seen when page is visited
  useEffect(() => {
    markAsSeen();
  }, [markAsSeen]);

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'responded': return 'info';
      case 'confirmed': return 'success';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  // Get status counts
  const statusCounts = {
    all: inquiries.length,
    pending: inquiries.filter(i => i.status === 'pending').length,
    responded: inquiries.filter(i => i.status === 'responded').length,
    confirmed: inquiries.filter(i => i.status === 'confirmed').length,
    declined: inquiries.filter(i => i.status === 'declined').length,
  };

  return (
    <AuthGuard requiresHost={true}>
      <SidebarLayout>
        <Box>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Booking Inquiries
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage booking requests from potential guests
            </Typography>
          </Box>

          {/* Status Filter Tabs */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: 'All', count: statusCounts.all },
                { key: 'pending', label: 'Pending', count: statusCounts.pending },
                { key: 'responded', label: 'Responded', count: statusCounts.responded },
                { key: 'confirmed', label: 'Confirmed', count: statusCounts.confirmed },
                { key: 'declined', label: 'Declined', count: statusCounts.declined },
              ].map(({ key, label, count }) => (
                <Chip
                  key={key}
                  label={`${label} (${count})`}
                  variant={selectedStatus === key ? 'filled' : 'outlined'}
                  color={selectedStatus === key ? 'primary' : 'default'}
                  onClick={() => setSelectedStatus(key)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Inquiries List */}
              {filteredInquiries.length === 0 ? (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No inquiries found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedStatus === 'all'
                        ? 'You haven\'t received any booking inquiries yet.'
                        : `No ${selectedStatus} inquiries at the moment.`
                      }
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Grid container spacing={3}>
                  {filteredInquiries.map((inquiry) => (
                    <Grid item xs={12} key={inquiry.inquiryId}>
                      <Card sx={{ position: 'relative' }}>
                        <CardContent>
                          {/* Header with Status and Actions */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: 'primary.main' }}>
                                {inquiry.guestInfo.name.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="h6" fontWeight="bold">
                                  {inquiry.guestInfo.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(inquiry.createdAt)}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={inquiry.status.charAt(0).toUpperCase() + inquiry.status.slice(1)}
                                color={getStatusColor(inquiry.status) as any}
                                size="small"
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuClick(e, inquiry.inquiryId)}
                                disabled={(updateInquiryStatusMutation.isPending || createBookingMutation.isPending) && selectedInquiry === inquiry.inquiryId}
                              >
                                {(updateInquiryStatusMutation.isPending || createBookingMutation.isPending) && selectedInquiry === inquiry.inquiryId ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <MoreVertIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Property Info */}
                          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="subtitle2" fontWeight="bold">
                                {inquiry.listingTitle}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Stay Details */}
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon fontSize="small" color="action" />
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Check-in
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatDate(inquiry.stayDetails.checkInDate)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon fontSize="small" color="action" />
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Check-out
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatDate(inquiry.stayDetails.checkOutDate)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PeopleIcon fontSize="small" color="action" />
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Guests
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {inquiry.stayDetails.guestCount}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Contact Information */}
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Contact Information
                          </Typography>
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {inquiry.guestInfo.email}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WhatsAppIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {inquiry.guestInfo.whatsappNumber}
                                </Typography>
                              </Box>
                            </Grid>
                            {inquiry.guestInfo.instagramId && (
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <InstagramIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {inquiry.guestInfo.instagramId}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                          </Grid>

                          {/* Additional Notes */}
                          {inquiry.additionalNotes && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Additional Notes
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {inquiry.additionalNotes}
                              </Typography>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}

          {/* Action Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {(() => {
              const currentInquiry = selectedInquiry ? inquiries.find(i => i.inquiryId === selectedInquiry) : null;
              const currentStatus = currentInquiry?.status;
              const isLoading = updateInquiryStatusMutation.isPending || createBookingMutation.isPending;

              return (
                <>
                  {currentStatus === 'pending' && (
                    <MenuItem
                      onClick={() => selectedInquiry && updateInquiryStatus(selectedInquiry, 'responded')}
                      disabled={isLoading}
                    >
                      Mark as Responded
                    </MenuItem>
                  )}
                  {(currentStatus === 'pending' || currentStatus === 'responded') && (
                    <MenuItem
                      onClick={() => currentInquiry && openBookingConfirmation(currentInquiry)}
                      disabled={isLoading}
                    >
                      Confirm Booking
                    </MenuItem>
                  )}
                  {(currentStatus === 'pending' || currentStatus === 'responded') && (
                    <MenuItem
                      onClick={() => selectedInquiry && updateInquiryStatus(selectedInquiry, 'declined')}
                      disabled={isLoading}
                    >
                      Decline Inquiry
                    </MenuItem>
                  )}
                </>
              );
            })()}
          </Menu>

          {/* Booking Confirmation Form */}
          <BookingForm
            open={bookingFormOpen}
            onClose={handleBookingFormClose}
            onSubmit={handleBookingSubmit}
            listings={listings}
            isEditMode={false}
            booking={inquiryToConfirm ? {
              bookingId: '', // New booking, no ID yet
              listingId: inquiryToConfirm.listingId,
              hostId: '', // Will be set by backend
              guestId: inquiryToConfirm.guestInfo.email, // Use email as guestId
              startDate: inquiryToConfirm.stayDetails.checkInDate,
              endDate: inquiryToConfirm.stayDetails.checkOutDate,
              guests: inquiryToConfirm.stayDetails.guestCount,
              totalPrice: 0, // Will be automatically calculated
              status: 'pending' as const,
              paymentStatus: 'pending' as const,
              primaryGuestContact: {
                name: inquiryToConfirm.guestInfo.name,
                email: inquiryToConfirm.guestInfo.email,
                whatsappNumber: inquiryToConfirm.guestInfo.whatsappNumber,
                preferredMethod: (inquiryToConfirm.guestInfo.whatsappNumber ? 'both' : 'email') as 'both' | 'email' | 'whatsapp',
              },
              expectedGuests: inquiryToConfirm.stayDetails.guestCount,
              guestCheckInStatus: {
                completed: 0,
                pending: inquiryToConfirm.stayDetails.guestCount,
                lastUpdated: new Date().toISOString(),
              },
              communicationLog: [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            } : null}
          />

          {/* Success Snackbar */}
          <Snackbar
            open={showSuccess}
            autoHideDuration={6000}
            onClose={() => setShowSuccess(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setShowSuccess(false)}
              severity="success"
              sx={{ width: '100%' }}
            >
              {successMessage}
            </Alert>
          </Snackbar>
        </Box>
      </SidebarLayout>
    </AuthGuard>
  );
};

export default InquiriesPage;